/**
 * **For the crew** — `docs/visit-page.md` §13, Board 9 (do-not-substitute
 * 11): everything the crew should know, rolled up, never copied.
 *
 * A note lives where it is true — the contact, the property, the job (the
 * quote's crew note, kept at approval), the visit — and this tab shows each
 * with its source chip and a link to its home. Internal notes never roll up,
 * and the dashed line says so. Files and map layers the crew may see roll up
 * the same way. On the right, the 400-wide card in its bezel, *What Marco sees*: the crew iPad's
 * card, read from the crew app's own endpoint as the lead (§13.4).
 *
 * Reads `GET /visits/:id/for-crew` (`ForCrewRead`); writes **Add note**
 * (`POST …/crew-notes`), a note's **Edit** (`PATCH /crew-notes/:id`, asking
 * *How far* on a series visit) and **Add a file for the crew** (`POST
 * …/crew-files`), each answering this tab's fresh read with the header and
 * the counts (§5.2).
 *
 * Desktop at 1440 (Board 9); an iPad panel narrower than the two columns
 * stacks the preview under the files and maps (§19.1, cards measured by the
 * panel); a phone draws the same cards in one column (§19.2), the composer
 * opening from the green **Add note** too (`?note=1`, lead round 5).
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  LAYER_PALETTE, type CrewFile, type CrewMap, type CrewMapShape, type CrewNote, type ForCrewRead, type LayerColor,
  type VisitRead,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { useRecordUpload } from '../record/use-record-upload.js'
import { useVisitTab, useVisitWrite } from './api.js'
import { timeIn } from './format.js'
import { recentDay } from './crew-when.js'
import { VisitGlyph } from './glyphs.js'
import { GreenLink, PillChip, SectionHead, SourceChip, useBoxWidth } from './crew-parts.js'
import { CrewNoteComposerCard } from './crew-note-composer.js'
import { CrewNoteSheet } from './crew-note-sheet.js'
import { CrewIpadCardView } from './crew-ipad-card.js'
import { HowFarPopover } from './how-far.js'
import { COMMON, CREW_NOTE, CREW_PREVIEW, FOR_CREW } from './copy.js'

type Filter = 'all' | 'notes' | 'files' | 'maps'

/** The preview stands beside the notes only where both fit (1092 in an iPad's landscape panel; 1392 at 1440). */
const BESIDE_FROM = 1000

export function ForCrewTab({ visitId, read, phone }: { visitId: string; read: VisitRead; phone: boolean }) {
  const { data, refresh } = useVisitTab<ForCrewRead>(visitId, '/for-crew')
  const write = useVisitWrite(visitId)
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [filter, setFilter] = useState<Filter>('all')
  const [box, width] = useBoxWidth()
  const filesAt = useRef<HTMLDivElement>(null)
  const mapsAt = useRef<HTMLDivElement>(null)

  /*
   * Two ways in by address (lead rounds 5, 6): the phone's green *Add note*
   * arrives as `?note=1` and opens the composer's sheet; the Overview's *Edit
   * the note* (Board 14) as `?edit=<activity>` and opens that note in its edit
   * mode, scrolled in. Each is read once and taken off the address.
   */
  const [noteSheet, setNoteSheet] = useState(params.get('note') === '1')
  const [editFirst] = useState(params.get('edit'))
  useEffect(() => {
    if (params.get('note') === null && params.get('edit') === null) return
    const next = new URLSearchParams(params)
    next.delete('note')
    next.delete('edit')
    setParams(next, { replace: true })
  }, [params, setParams])

  /*
   * A note written from the header's *Message crew* (answered with the
   * Overview's read) moves this tab's count; read the tab again when it does.
   */
  const count = read.tabs.find((tab) => tab.key === 'for-crew')?.count
  const seen = useRef(count)
  useEffect(() => {
    if (seen.current === count) return
    seen.current = count
    refresh()
  }, [count, refresh])

  const upload = useRecordUpload({
    filesAt: null,
    orgUnitId: read.orgUnitId,
    canEdit: Boolean(data?.canAddFile),
    describe: false,
    onUploaded: (files) => {
      if (files.length === 0) return
      void write('POST', '/crew-files', { fileIds: files.map((file) => file.id) }, { tab: 'for-crew' })
        .then((result) => { if (result.tab) refresh(result.tab as ForCrewRead) })
    },
  })

  if (!data) return <div ref={box} />

  const beside = !phone && width >= BESIDE_FROM
  const main = beside ? width - 424 : width
  const showNotes = filter === 'all' || filter === 'notes'
  const showFiles = filter === 'all' || filter === 'files'
  const showMaps = filter === 'all' || filter === 'maps'
  const propertyId = data.composer.property?.id ?? read.header.meta.property?.id ?? null

  const chips: { key: Filter; label: string }[] = [
    { key: 'all', label: FOR_CREW.chips.all(String(data.counts.all)) },
    { key: 'notes', label: FOR_CREW.chips.notes(String(data.counts.notes)) },
    { key: 'files', label: FOR_CREW.chips.files(String(data.counts.files)) },
    { key: 'maps', label: FOR_CREW.chips.maps(String(data.counts.maps)) },
  ]

  const preview = (
    /* The card is 400 inside its 8px bezel — 416 across, as Board 9 lays it out. */
    <div className={cn('flex flex-col', beside && 'w-[416px] shrink-0')}>
      <div className="mb-2.5 flex items-baseline gap-2.5">
        {data.preview.viewer && (
          <b className="text-[13.5px] font-bold text-text">{CREW_PREVIEW.title(data.preview.viewer.firstName)}</b>
        )}
        <span className="text-[12px] text-text-faint">{CREW_PREVIEW.when}</span>
      </div>
      <CrewIpadCardView
        card={data.preview}
        onPlans={() => { setFilter('all'); filesAt.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }) }}
        onMap={() => { setFilter('all'); mapsAt.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }) }}
        onLoadList={() => navigate(`/visits/${encodeURIComponent(visitId)}?tab=materials&view=day`)}
      />
    </div>
  )

  const column = (
    <div className="flex min-w-0 flex-1 flex-col gap-3.5">
      <div className={cn('flex gap-2.5', phone ? 'flex-col gap-1.5' : 'items-center')}>
        <div className={cn('flex gap-1.5', phone ? 'no-scrollbar -mx-4 overflow-x-auto px-4' : 'flex-wrap')}>
          {chips.map((chip) => (
            <PillChip key={chip.key} look={phone ? 'phone' : 'filter'} selected={filter === chip.key}
              onClick={() => setFilter(chip.key)}>
              {chip.label}
            </PillChip>
          ))}
        </div>
        {!phone && <span className="flex-1" />}
        <span className="text-[12px] text-text-muted">{FOR_CREW.sub}</span>
      </div>

      {showNotes && data.composer.can && (
        <CrewNoteComposerCard
          composer={data.composer}
          orgUnitId={read.orgUnitId}
          write={write}
          tab="for-crew"
          phone={phone}
          onWritten={(result) => { if (result.tab) refresh(result.tab as ForCrewRead) }}
        />
      )}

      {showNotes && data.notes.length > 0 && (
        <>
          <SectionHead
            title={FOR_CREW.notes}
            count={data.notes.length}
            right={<span className="text-[12px] text-text-muted">{FOR_CREW.notesSub}</span>}
          />
          {data.notes.map((note) => (
            <NoteCard
              key={`${note.source}:${note.id}`}
              note={note}
              read={read}
              phone={phone}
              onOpen={(to) => navigate(to)}
              onSaved={(next) => refresh(next)}
              startEditing={note.home.kind === 'visit' && note.home.activityId === editFirst}
              visitId={visitId}
            />
          ))}
        </>
      )}

      {showNotes && data.internalNotes.count > 0 && (
        <div className="flex items-center gap-2 rounded-[10px] border border-dashed border-line-strong bg-surface-alt px-3 py-[9px] text-[12px] text-text-muted">
          <VisitGlyph name="lock" size={13} stroke={2} />
          {FOR_CREW.internalStay(String(data.internalNotes.count), data.internalNotes.on.map((one) => one.reference))}
        </div>
      )}

      {showFiles && (data.files.length > 0 || data.canAddFile) && (
        <div ref={filesAt} className="flex scroll-mt-4 flex-col gap-3.5">
          <input {...upload.inputProps} />
          <SectionHead
            title={FOR_CREW.files}
            count={data.files.length}
            right={data.canAddFile
              ? (
                <GreenLink glyph="plus" onClick={() => upload.openPicker({ visibility: 'crew' })}>
                  {FOR_CREW.addFile}
                </GreenLink>
              )
              : null}
          />
          {upload.error && <p className="m-0 text-[12px] text-danger">{upload.error}</p>}
          {data.files.length > 0 && (
            <div className={cn('grid gap-2.5', phone ? 'grid-cols-1' : main >= 720 ? 'grid-cols-3' : 'grid-cols-2')}>
              {data.files.map((file) => <FileTile key={file.file.id} file={file} read={read} />)}
            </div>
          )}
        </div>
      )}

      {showMaps && data.maps.length > 0 && (
        <div ref={mapsAt} className="flex scroll-mt-4 flex-col gap-3.5">
          <SectionHead
            title={FOR_CREW.maps}
            count={data.maps.length}
            right={<span className="text-[12px] text-text-muted">{FOR_CREW.mapsSub}</span>}
          />
          <div className={cn('grid gap-2.5', phone ? 'grid-cols-1' : 'grid-cols-2')}>
            {data.maps.map((map) => (
              <MapTile
                key={map.layerId}
                map={map}
                read={read}
                onOpen={propertyId ? () => navigate(`/properties/${propertyId}?tab=map`) : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {!beside && preview}
    </div>
  )

  return (
    <div ref={box} data-as-drawn="vp-09 A" className={cn('flex items-start', beside ? 'gap-6' : 'flex-col')}>
      {column}
      {beside && preview}
      {noteSheet && (
        <CrewNoteSheet
          visitId={visitId}
          read={read}
          tab="for-crew"
          onClose={() => setNoteSheet(false)}
          onWritten={(result) => { if (result.tab) refresh(result.tab as ForCrewRead) }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// A note, with its source, its line and its way home
// ---------------------------------------------------------------------------

function NoteCard({
  note, read, phone, visitId, onOpen, onSaved, startEditing = false,
}: {
  note: CrewNote
  read: VisitRead
  phone: boolean
  visitId: string
  onOpen: (to: string) => void
  /** Opened by `?edit=` — in edit mode, scrolled into view. */
  startEditing?: boolean
  onSaved: (next: ForCrewRead) => void
}) {
  const write = useVisitWrite(visitId)
  const [editing, setEditing] = useState(startEditing && read.can.edit)
  const card = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (startEditing) card.current?.scrollIntoView({ block: 'center' })
  }, [startEditing])
  const [draft, setDraft] = useState(note.text)
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const saveAt = useRef<HTMLButtonElement>(null)
  const tz = read.timeZone
  const byLine = note.by && note.at ? COMMON.dot(note.by.name, timeIn(tz, note.at, 'dayAmpm')) : null

  const head = (() => {
    const home = note.home
    switch (home.kind) {
      case 'visit':
        return {
          title: byLine ?? '',
          sub: note.pinned ? FOR_CREW.visitPinned : FOR_CREW.visitOnly,
          link: home.editable && read.can.edit
            ? <GreenLink glyph="pencil" onClick={() => { setDraft(note.text); setEditing(true) }}>{FOR_CREW.edit}</GreenLink>
            : null,
        }
      case 'property':
        return {
          title: home.field === 'access_notes' ? home.line1 : byLine ?? home.line1,
          sub: FOR_CREW.propertySub,
          link: (
            <GreenLink glyph="chevronRight"
              onClick={() => onOpen(`/properties/${home.propertyId}?${home.field === 'access_notes' ? 'focus=site' : 'tab=activity'}`)}>
              {FOR_CREW.editOnProperty}
            </GreenLink>
          ),
        }
      case 'contact':
        return {
          title: home.field === 'access_note' ? home.name : byLine ?? home.name,
          sub: home.field === 'access_note' ? FOR_CREW.contactSub(home.firstName) : CREW_NOTE.home.contact(home.firstName),
          link: (
            <GreenLink glyph="chevronRight"
              onClick={() => onOpen(`/contacts/${home.contactId}${home.field === 'access_note' ? '' : '?tab=activity'}`)}>
              {FOR_CREW.editOnContact}
            </GreenLink>
          ),
        }
      case 'job':
        return {
          title: home.field === 'crew_note' && home.quoteReference
            ? FOR_CREW.jobTitle(home.reference, home.quoteReference)
            : byLine ?? home.reference,
          sub: home.field === 'crew_note' ? FOR_CREW.jobSub : home.reference,
          link: (
            <GreenLink glyph="chevronRight"
              onClick={() => onOpen(`/jobs/${home.jobId}${home.field === 'crew_note' ? '' : '?tab=activity'}`)}>
              {FOR_CREW.openJob(home.reference)}
            </GreenLink>
          ),
        }
    }
  })()

  async function save(applyTo: 'this' | 'this_and_after') {
    if (note.home.kind !== 'visit' || !draft.trim()) return
    setBusy(true)
    setError(null)
    try {
      const result = await write('PATCH', `/crew-notes/${note.home.activityId}`, { text: draft.trim(), applyTo },
        { tab: 'for-crew' })
      setEditing(false)
      setAsking(false)
      if (result.tab) onSaved(result.tab as ForCrewRead)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  /* A series visit asks how far the change goes (Board 14, D20); a one-off just saves. */
  const series = read.series
  const asksHowFar = Boolean(series && series.after.count > 0)
  const today = read.header.meta.dates.first

  return (
    <div ref={card} className="flex flex-col gap-[7px] rounded-[12px] border border-line bg-surface px-3.5 py-3">
      <div className={cn('flex gap-2', phone ? 'flex-wrap items-center' : 'items-center')}>
        <SourceChip source={note.source} />
        {head.title && <b className="min-w-0 truncate text-[12.5px] font-bold text-text">{head.title}</b>}
        <span className="flex-1" />
        <span className={cn('text-[11.5px] text-text-faint', phone && 'basis-full')}>{head.sub}</span>
      </div>
      {editing ? (
        <textarea
          aria-label={CREW_NOTE.title}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          className="min-h-[58px] w-full resize-y rounded-button border border-accent-line bg-surface px-3 py-2.5 text-[13px] leading-[1.5] text-text outline-none shadow-[0_0_0_2px_rgb(var(--color-accent-ring))]"
        />
      ) : (
        <div className={cn('whitespace-pre-line leading-[1.5] text-text', phone ? 'text-[14px]' : 'text-[13px]')}>{note.text}</div>
      )}
      {error && <p className="m-0 text-[12px] text-danger">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        {editing ? (
          <>
            <button type="button" onClick={() => { setEditing(false); setAsking(false) }}
              className="inline-flex h-[30px] items-center rounded-button border border-line bg-surface px-3.5 text-[12px] font-semibold text-text hover:bg-hover">
              {COMMON.cancel}
            </button>
            <button ref={saveAt} type="button" disabled={busy || !draft.trim()}
              onClick={() => (asksHowFar ? setAsking(true) : void save('this'))}
              className="inline-flex h-[30px] items-center rounded-button border border-accent bg-accent px-3.5 text-[12px] font-semibold text-white hover:bg-accent-dark disabled:opacity-60">
              {COMMON.save}
            </button>
          </>
        ) : head.link}
      </div>
      {asking && series && today && (
        <HowFarPopover
          anchor={saveAt.current}
          series={series}
          date={today}
          busy={busy}
          onSave={(applyTo) => void save(applyTo)}
          onCancel={() => setAsking(false)}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Files and maps
// ---------------------------------------------------------------------------

function FileTile({ file, read }: { file: CrewFile; read: VisitRead }) {
  const when = recentDay(read.timeZone, file.at, read.now)
  const line = file.pages === null ? when
    : file.pages === 1 ? FOR_CREW.page(String(file.pages), when) : FOR_CREW.pages(String(file.pages), when)
  const pdf = file.file.contentType === 'application/pdf'
  const image = file.file.contentType.startsWith('image/')
  const extension = file.file.name.includes('.') ? file.file.name.split('.').pop()!.toUpperCase() : ''
  return (
    <a
      href={file.file.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex min-w-0 items-center gap-2.5 rounded-[12px] border border-line bg-surface p-3 hover:bg-surface-alt"
    >
      <span
        className={cn('inline-flex h-11 w-9 shrink-0 items-center justify-center overflow-hidden rounded-[6px] text-[9.5px] font-extrabold',
          pdf ? 'bg-danger-bg text-danger' : 'bg-line-soft text-text-muted')}
      >
        {image ? <img src={file.file.url} alt="" className="h-full w-full object-cover" /> : pdf ? FOR_CREW.pdf : extension}
      </span>
      <span className="flex min-w-0 flex-col">
        <b className="truncate text-[12.5px] font-bold text-text">{file.file.name}</b>
        <span className="text-[11.5px] text-text-muted">{line}</span>
        <span className="mt-1 flex items-center gap-1"><SourceChip source={file.source} /></span>
      </span>
    </a>
  )
}

/**
 * A layer drawn small — the server's shapes in the board's 540 × 120 box
 * (Board 9): buildings white with their grey edge, hardscape grey (firm when
 * the layer's own, faint as another layer's setting), the layer's features in
 * their colours, a measure as a fine dashed line.
 */
const INK = {
  paper: 'rgb(var(--color-map-paper))',
  building: 'rgb(var(--color-surface))',
  buildingEdge: 'rgb(var(--color-line-strong))',
  hardscape: 'rgb(var(--color-map-hardscape))',
  hardscapeEdge: 'rgb(var(--color-map-hardscape-edge))',
  wall: 'rgb(var(--color-map-wall))',
  muted: 'rgb(var(--color-text-muted))',
  ink: 'rgb(var(--color-text))',
}

const points = (list: [number, number][]): string => list.map((point) => point.join(',')).join(' ')

/**
 * A label's words in a darker ink of its colour where the board writes one —
 * *Fire pit* in the amber band's text, *12 ft* in the green's (Board 9) — and
 * in the colour itself otherwise (*Water*).
 */
const LABEL_INK: Partial<Record<LayerColor, string>> = {
  amber: 'rgb(var(--color-amber-band-text))',
  green: 'rgb(var(--color-green-band-text))',
  teal: 'rgb(var(--color-teal-band-text))',
  slate: 'rgb(var(--color-slate-band-text))',
}

function MapPreview({ shapes }: { shapes: CrewMapShape[] }) {
  return (
    <svg width="100%" height="120" viewBox="0 0 540 120" preserveAspectRatio="xMidYMid slice" className="block font-sans">
      <rect width="540" height="120" fill={INK.paper} />
      {shapes.map((shape, index) => {
        const key = `${shape.kind}-${index}`
        const colour = 'colour' in shape && shape.colour ? LAYER_PALETTE[shape.colour] : INK.ink
        switch (shape.kind) {
          case 'area':
            if (shape.role === 'building') {
              return <polygon key={key} points={points(shape.points)} fill={INK.building} stroke={INK.buildingEdge} />
            }
            if (shape.role === 'hardscape') {
              return (
                <polygon key={key} points={points(shape.points)} fill={INK.hardscape} opacity={shape.own ? 0.55 : 0.35}
                  {...(shape.own ? { stroke: INK.hardscapeEdge } : {})} />
              )
            }
            return <polygon key={key} points={points(shape.points)} fill={colour} fillOpacity={0.35} stroke={colour} />
          case 'line':
            if (shape.role === 'hardscape') {
              return <polyline key={key} points={points(shape.points)} fill="none" stroke={INK.wall} strokeWidth={5} />
            }
            if (shape.role === 'measure') {
              return <polyline key={key} points={points(shape.points)} fill="none" stroke={colour} strokeDasharray="3 3" />
            }
            return (
              <polyline key={key} points={points(shape.points)} fill="none" stroke={colour} strokeWidth={3}
                {...(shape.dashed ? { strokeDasharray: '7 4' } : {})} />
            )
          case 'marker':
            return <circle key={key} cx={shape.at[0]} cy={shape.at[1]} r={12} fill={LAYER_PALETTE[shape.colour]} opacity={0.85} />
          case 'label':
            return (
              <text key={key} x={shape.at[0]} y={shape.at[1]} textAnchor={shape.anchor}
                fontSize={shape.role === 'measure' ? 9 : 10} fontWeight={shape.role === 'building' || shape.role === 'measure' ? 400 : 700}
                fill={shape.role === 'building' ? INK.muted : shape.role === 'hardscape' ? INK.ink
                  : (shape.colour && LABEL_INK[shape.colour]) || colour}>
                {shape.text}
              </text>
            )
        }
      })}
    </svg>
  )
}

function MapTile({ map, read, onOpen }: { map: CrewMap; read: VisitRead; onOpen: (() => void) | undefined }) {
  const when = recentDay(read.timeZone, map.at, read.now)
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!onOpen}
      className="flex min-w-0 flex-col overflow-hidden rounded-[12px] border border-line bg-surface text-left"
    >
      {/* The drawing is 120 tall over its 1px rule (Board 9 draws the rule outside the 120). */}
      <span className="block h-[121px] w-full overflow-hidden border-b border-line-faint">
        <MapPreview shapes={map.preview} />
      </span>
      <span className="flex w-full items-center gap-2 px-3 py-2.5">
        <span className="flex min-w-0 flex-col">
          <b className="truncate text-[12.5px] font-bold text-text">{map.name}</b>
          <span className="text-[11.5px] text-text-muted">
            {map.drawnBy ? FOR_CREW.drawnBy(map.drawnBy.name, when) : when}
          </span>
        </span>
        <span className="flex-1" />
        <SourceChip source={map.source} />
      </span>
    </button>
  )
}
